import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Loader2, LogOut, TrendingUp, FileDown, FileText, Image as ImageIcon,
  BadgeCheck, CarFront, CalendarCheck, KeyRound, CreditCard, Receipt, Award, Gift, Copy, MessageCircle,
} from "lucide-react";
import {
  portalLogin, portalMe, setPortalToken, clearPortalToken, getPortalToken,
  portalMediaUrl, statementPdfUrl, instalmentCheckout, settlementCheckout, receiptPdfUrl, certificatePdfUrl, zar, formatApiErrorDetail,
} from "@/api";

const DOC_LABELS = { id_copy: "ID copy", licence_copy: "Driver's licence", proof_of_income: "Proof of income" };

const STATUS_LABELS = {
  new: "Application received",
  "agreement-sent": "Agreement awaiting your signature",
  "agreement-signed": "Agreement signed — on the road to ownership",
  contacted: "We're in touch",
  "in-progress": "Verification in progress",
  closed: "Completed",
};

export default function Portal() {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(Boolean(getPortalToken()));
  const [form, setForm] = useState({ reference: "", id_number: "" });
  const [signingIn, setSigningIn] = useState(false);
  const [paying, setPaying] = useState(false);

  const payInstalment = async () => {
    setPaying(true);
    try {
      const res = await instalmentCheckout(window.location.origin);
      window.location.href = res.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setPaying(false);
    }
  };

  const [settling, setSettling] = useState(false);
  const paySettlement = async () => {
    setSettling(true);
    try {
      const res = await settlementCheckout(window.location.origin);
      window.location.href = res.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setSettling(false);
    }
  };

  const load = () => {
    portalMe()
      .then(setMe)
      .catch(() => {
        clearPortalToken();
        setMe(null);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (getPortalToken()) load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSigningIn(true);
    try {
      const res = await portalLogin(form);
      setPortalToken(res.portal_token);
      toast.success(`Welcome back, ${res.full_name?.split(" ")[0] || "driver"}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSigningIn(false);
    }
  };

  const openDoc = async (path) => {
    try {
      const res = await fetch(portalMediaUrl(path), { headers: { Authorization: `Bearer ${getPortalToken()}` } });
      if (!res.ok) throw new Error();
      window.open(URL.createObjectURL(await res.blob()), "_blank");
    } catch {
      toast.error("Could not open document");
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-slate-400" data-testid="portal-loading">Loading your portal…</div>;
  }

  if (!me) {
    return (
      <div data-testid="portal-login-page" className="min-h-screen flex items-center justify-center px-4 pt-20">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-md bg-[#122659]/80 backdrop-blur-xl border border-sky-300/20 rounded-xl p-8 md:p-10"
        >
          <div className="flex flex-col items-center mb-8 text-center">
            <KeyRound size={28} className="text-[#F57C00] mb-4" />
            <h1 className="font-display text-3xl font-black text-white">Client portal</h1>
            <p className="text-sm text-slate-400 mt-2">
              Sign in with your application reference (e.g. CCM-AB12CD) and your ID number.
            </p>
          </div>
          <form onSubmit={submit} data-testid="portal-login-form" className="space-y-5">
            <div>
              <label className="input-label" htmlFor="pl-ref">Application reference</label>
              <input id="pl-ref" data-testid="portal-ref-input" required className="input-field uppercase" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="CCM-XXXXXX" />
            </div>
            <div>
              <label className="input-label" htmlFor="pl-id">ID / passport number</label>
              <input id="pl-id" data-testid="portal-id-input" required className="input-field" value={form.id_number} onChange={(e) => setForm({ ...form, id_number: e.target.value })} placeholder="9001014800082" />
            </div>
            <button type="submit" data-testid="portal-login-btn" disabled={signingIn} className="w-full bg-[#F57C00] hover:bg-[#E67300] disabled:opacity-60 text-white font-bold px-6 py-4 rounded-md transition-colors duration-300 flex items-center justify-center gap-2">
              {signingIn && <Loader2 size={18} className="animate-spin" />}
              {signingIn ? "Signing in…" : "Sign in"}
            </button>
          </form>
          <p className="text-xs text-slate-500 mt-6 text-center">Lost your reference? Call 010 534 9926.</p>
        </motion.div>
      </div>
    );
  }

  const st = me.statement;

  return (
    <div data-testid="portal-dashboard" className="pt-32 pb-24 min-h-screen">
      <div className="w-full max-w-4xl mx-auto px-4 sm:px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="flex flex-wrap items-start justify-between gap-4 mb-10">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-sky-300 font-bold mb-2">Client portal</p>
              <h1 className="font-display text-4xl sm:text-5xl tracking-tight font-black text-white">
                Hello, {me.full_name?.split(" ")[0]}.
              </h1>
              <p className="text-sm text-slate-400 mt-2">
                Reference <strong className="text-[#F57C00]">{me.reference}</strong>
                {(me.vehicle?.make || me.vehicle?.model) && <> · {me.vehicle.make} {me.vehicle.model} · {me.vehicle.term} months</>}
              </p>
            </div>
            <button
              data-testid="portal-logout-btn"
              onClick={() => { clearPortalToken(); setMe(null); }}
              className="inline-flex items-center gap-2 border border-sky-300/30 text-slate-300 hover:text-white hover:border-sky-300 text-sm font-bold px-5 py-3 rounded-md transition-colors"
            >
              <LogOut size={16} /> Sign out
            </button>
          </div>

          <div className="inline-flex items-center gap-2 bg-[#122659] border border-sky-300/20 rounded-full px-5 py-2.5 mb-10" data-testid="portal-status">
            <BadgeCheck size={16} className="text-[#F57C00]" />
            <span className="text-sm font-semibold text-white">{STATUS_LABELS[me.status] || me.status}</span>
          </div>

          {st && (
            <div className="bg-[#122659] border border-sky-300/20 rounded-xl p-8 mb-6" data-testid="portal-progress-card">
              <div className="flex items-center gap-3 mb-5">
                <TrendingUp size={22} className="text-[#F57C00]" />
                <p className="font-display text-lg font-bold text-white">Ownership progress</p>
              </div>
              <div className="h-4 rounded-full bg-[#0B1F4E] border border-sky-300/20 overflow-hidden mb-3">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${st.progress}%` }}
                  transition={{ duration: 1.2, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
                  className="h-full bg-[#F57C00] rounded-full"
                  data-testid="portal-progress-bar"
                />
              </div>
              <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                  <p className="font-display text-3xl font-black text-[#F57C00]" data-testid="portal-progress-pct">{st.progress}%</p>
                  <p className="text-xs text-slate-500 mt-1">
                    {zar(st.paid)} paid of {zar(st.total)} · {zar(st.remaining)} to go
                  </p>
                </div>
                {me.statement_token && (
                  <a href={statementPdfUrl(me.statement_token)} data-testid="portal-statement-pdf" className="inline-flex items-center gap-2 border-2 border-[#87CEEB] text-[#87CEEB] hover:bg-[#87CEEB] hover:text-[#0B1F4E] text-sm font-bold px-5 py-3 rounded-md transition-colors">
                    <FileDown size={16} /> Statement PDF
                  </a>
                )}
              </div>
              {me.debit_day && (
                <p className="text-xs text-slate-400 mt-5 flex items-center gap-2">
                  <CalendarCheck size={14} className="text-sky-300" />
                  Your debit order runs on day {me.debit_day} of each month.
                </p>
              )}
            </div>
          )}

          {me.status === "agreement-signed" && Number(me.vehicle?.max_monthly) > 0 && (
            <div className="bg-[#122659] border border-[#F57C00]/30 rounded-xl p-7 mb-6 flex flex-wrap items-center justify-between gap-4" data-testid="portal-instalment-card">
              <div>
                <p className="text-xs uppercase tracking-widest text-sky-300 font-bold mb-1">This month's instalment</p>
                <p className="font-display text-2xl font-black text-white">{zar(Number(me.vehicle.max_monthly))}</p>
                <p className="text-xs text-slate-500 mt-1">Paid by card? It's logged to your ownership progress automatically.</p>
              </div>
              <button
                data-testid="portal-pay-instalment-btn"
                onClick={payInstalment}
                disabled={paying}
                className="inline-flex items-center gap-2 bg-[#F57C00] hover:bg-[#E67300] disabled:opacity-60 text-white font-bold px-6 py-3.5 rounded-md transition-colors duration-300"
              >
                {paying ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />}
                {paying ? "Opening secure checkout…" : "Pay online by card"}
              </button>
            </div>
          )}

          {me.status === "agreement-signed" && me.statement && me.statement.remaining > 0 && (
            <div className="bg-[#122659] border border-emerald-400/30 rounded-xl p-7 mb-6 flex flex-wrap items-center justify-between gap-4" data-testid="portal-settlement-card">
              <div>
                <p className="text-xs uppercase tracking-widest text-emerald-300 font-bold mb-1">Early settlement figure</p>
                <p className="font-display text-2xl font-black text-white">{zar(me.statement.remaining)}</p>
                <p className="text-xs text-slate-500 mt-1">Pay it off now and the vehicle gets registered in your name — no more monthly payments.</p>
              </div>
              <button
                data-testid="portal-pay-settlement-btn"
                onClick={paySettlement}
                disabled={settling}
                className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 text-white font-bold px-6 py-3.5 rounded-md transition-colors duration-300"
              >
                {settling ? <Loader2 size={16} className="animate-spin" /> : <BadgeCheck size={16} />}
                {settling ? "Opening secure checkout…" : "Settle & own it"}
              </button>
            </div>
          )}

          {me.status === "closed" && (
            <div className="bg-emerald-500/10 border border-emerald-400/40 rounded-xl p-7 mb-6" data-testid="portal-settled-banner">
              <div className="flex items-center gap-4">
                <BadgeCheck size={28} className="text-emerald-400 shrink-0" />
                <div>
                  <p className="font-display text-xl font-black text-white">Ownership complete — congratulations!</p>
                  <p className="text-sm text-slate-400 mt-1">Your balance is settled. CompCar Motors will finalise registration of the vehicle in your name.</p>
                </div>
              </div>
              {me.certificate_token && (
                <a
                  href={certificatePdfUrl(me.certificate_token)}
                  data-testid="portal-certificate-btn"
                  className="mt-5 inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-bold px-5 py-3 rounded-md transition-colors"
                >
                  <Award size={16} /> Download your Certificate of Ownership
                </a>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-[#122659]/60 border border-sky-300/20 rounded-xl p-7" data-testid="portal-payments">
              <p className="text-xs uppercase tracking-[0.2em] text-sky-300 font-bold mb-4">Payment history</p>
              {me.payments.length === 0 ? (
                <p className="text-sm text-slate-500">No payments recorded yet.</p>
              ) : (
                <div className="space-y-2">
                  {me.payments.map((p) => (
                    <div key={p.id} className="flex justify-between items-center text-sm border-b border-white/5 pb-2">
                      <span className="text-slate-400">{new Date(p.paid_on).toLocaleDateString("en-ZA", { day: "numeric", month: "short", year: "numeric" })}</span>
                      <span className="font-semibold text-white">{zar(p.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-[#122659]/60 border border-sky-300/20 rounded-xl p-7" data-testid="portal-documents">
              <p className="text-xs uppercase tracking-[0.2em] text-sky-300 font-bold mb-4">Your documents</p>
              {Object.keys(me.documents || {}).length === 0 ? (
                <p className="text-sm text-slate-500">No documents uploaded yet.</p>
              ) : (
                <div className="flex flex-wrap gap-2.5">
                  {Object.entries(me.documents).map(([key, path]) => (
                    <button key={key} data-testid={`portal-doc-${key}`} onClick={() => openDoc(path)} className="inline-flex items-center gap-2 border border-sky-300/30 text-sky-300 hover:bg-sky-300 hover:text-[#0B1F4E] text-xs font-bold px-4 py-2.5 rounded-md transition-colors">
                      {path.endsWith(".pdf") ? <FileText size={14} /> : <ImageIcon size={14} />}
                      {DOC_LABELS[key] || key}
                    </button>
                  ))}
                </div>
              )}
              {me.agreement_signed && (
                <p className="text-xs text-emerald-300 mt-5" data-testid="portal-agreement-signed">
                  Agreement signed {me.agreement_signed_at ? new Date(me.agreement_signed_at).toLocaleDateString("en-ZA", { day: "numeric", month: "long", year: "numeric" }) : ""} — safely archived.
                </p>
              )}
            </div>
            <div className="bg-[#122659]/60 border border-sky-300/20 rounded-xl p-7 md:col-span-2" data-testid="portal-receipts">
              <p className="text-xs uppercase tracking-[0.2em] text-sky-300 font-bold mb-4 flex items-center gap-2">
                <Receipt size={16} className="text-[#F57C00]" /> Your receipts
              </p>
              {(me.receipts || []).length === 0 ? (
                <p className="text-sm text-slate-500">No online payments yet — receipts appear here after each card payment.</p>
              ) : (
                <div className="space-y-2">
                  {me.receipts.map((r) => (
                    <div key={r.token} data-testid={`portal-receipt-${r.receipt_no}`} className="flex flex-wrap items-center justify-between gap-3 text-sm bg-[#0B1F4E] border border-sky-300/10 rounded-md px-4 py-3">
                      <span className="font-bold text-white">{r.receipt_no}</span>
                      <span className="text-slate-400">{r.description}</span>
                      <span className="text-slate-400">{r.date}</span>
                      <span className="font-semibold text-white">{zar(r.amount)}</span>
                      <a
                        href={receiptPdfUrl(r.token)}
                        data-testid={`portal-receipt-dl-${r.receipt_no}`}
                        className="inline-flex items-center gap-1.5 text-xs font-bold text-sky-300 hover:text-white transition-colors"
                      >
                        <FileDown size={14} /> PDF
                      </a>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="bg-[#122659]/60 border border-[#F57C00]/30 rounded-xl p-7 md:col-span-2" data-testid="portal-referral-card">
              <p className="text-xs uppercase tracking-[0.2em] text-sky-300 font-bold mb-4 flex items-center gap-2">
                <Gift size={16} className="text-[#F57C00]" /> Refer a friend
              </p>
              {me.referral_code ? (
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <p className="text-sm text-slate-300 mb-2">
                      Know someone who needs a car? Share your link — when they sign, you earn <strong className="text-[#F57C00]">R500</strong> off your balance or in cash.
                    </p>
                    <p className="font-mono text-sm text-sky-300 break-all" data-testid="portal-referral-link">
                      {window.location.origin}/pre-qualify?ref={me.referral_code}
                    </p>
                    <p className="text-xs text-slate-500 mt-2" data-testid="portal-referral-stats">
                      {me.referral_total || 0} referred · {me.referral_signed || 0} signed
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <button
                      data-testid="portal-referral-copy"
                      onClick={() => {
                        navigator.clipboard.writeText(`${window.location.origin}/pre-qualify?ref=${me.referral_code}`);
                        toast.success("Referral link copied");
                      }}
                      className="inline-flex items-center gap-2 bg-[#F57C00] hover:bg-[#E67300] text-white text-sm font-bold px-5 py-3 rounded-md transition-colors"
                    >
                      <Copy size={15} /> Copy link
                    </button>
                    <a
                      data-testid="portal-referral-whatsapp"
                      href={`https://wa.me/?text=${encodeURIComponent(`Need a car? CompCar Motors got me driving with their rent-to-own plan — no bank drama. Apply with my link and I'll earn a reward when you sign: ${typeof window !== "undefined" ? window.location.origin : ""}/pre-qualify?ref=${me.referral_code}`)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-bold px-5 py-3 rounded-md transition-colors"
                    >
                      <MessageCircle size={15} /> Share on WhatsApp
                    </a>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-slate-500">Your referral link is being prepared — check back shortly.</p>
              )}
            </div>
          </div>

          <div className="mt-10 rounded-2xl border border-[#F57C00]/30 bg-gradient-to-r from-[#F57C00]/15 via-[#122659] to-[#122659] p-7 flex items-center gap-4">
            <CarFront size={26} className="text-[#F57C00] shrink-0" />
            <p className="text-sm text-slate-300">
              Every on-time payment builds your ownership. Questions about your plan? Call <strong className="text-white">010 534 9926</strong> or WhatsApp us anytime.
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
